// Port of SuiteStyle/StyleLinter.swift and StyleSettings (SuiteModel): the built-in style checks (cross-platform spec
// §11.2), each finding a candidate for a second look, never a score. All off by default.
//
//   - Filter verbs, dialogue tags (density, adverbs, fancy verbs): exact. Their patterns are the Swift's ICU ones with
//     `\b` spelt out as ICU's Unicode word boundary; `\s` is JavaScript's (it also takes U+000B and U+FEFF).
//   - Repeated words: APPROXIMATE without a real tagger (parts of speech and lemmas; see text/tagger.ts).
//   - Overused words: crutch words exact; crowded describing words APPROXIMATE (parts of speech) — overused.ts.
//   - Personal rules: Apple-only (personal.ts).
// Finding ids and messages are the Swift's. Offsets: UTF-16 while checking, Unicode scalars in findings.
import { spanClassRanges } from '../../findReplace.ts';
import { linkSyntaxRanges } from '../../proseMarkdown.ts';
import { sha256Hex } from '../../hashing.ts';
import { scalarOffsetsOfUTF16 } from '../../automerge/offsets.ts';
import { graphemes, isCFWhitespaceOrNewline, trimCFWhitespace } from '../../unicode.ts';
import type { ProjectRecord } from '../../model/documents.ts';
import { quotedRanges, quoteStyleOf, type QuoteStyle } from '../../text/dialogueBalance.ts';
import { charIsLowercase, intersects, type Range16 } from '../../text/scalars.ts';
import { sentenceRanges } from '../../text/sentences.ts';
import { approximateTagger, stemKey, type LanguageTagger, type LexicalClass } from '../../text/tagger.ts';
import { overusedWords } from './overused.ts';
import { scanTense, tenseFindings, type TenseScene } from './tense.ts';

export type BuiltInStyleCheck = 'repeated-words' | 'filter-verbs' | 'dialogue-tags' | 'overused-words' | 'tense';

export const BUILT_IN_STYLE_CHECKS: readonly BuiltInStyleCheck[] = ['repeated-words', 'filter-verbs', 'dialogue-tags', 'overused-words', 'tense'];

/** `BuiltInStyleCheck.pass`: the Revise room's pass a check belongs to — tense is a mistake, the rest style. */
export const styleCheckPass = (check: BuiltInStyleCheck): 'mistakes' | 'style' => (check === 'tense' ? 'mistakes' : 'style');

export const styleCheckName = (c: BuiltInStyleCheck): string =>
  ({ 'repeated-words': 'Repeated words', 'filter-verbs': 'Filter verbs', 'dialogue-tags': 'Dialogue tags', 'overused-words': 'Overused words', tense: 'Tense' })[c];

export const styleCheckSummary = (c: BuiltInStyleCheck): string => ({
  'repeated-words': 'The same word used again within a paragraph (names and common words aside).',
  'filter-verbs': "Saw, heard, felt, noticed… outside dialogue, where they can hold the reader at arm's length.",
  'dialogue-tags': 'Scenes where most lines of dialogue carry a tag, and tags dressed up with adverbs or fancy verbs.',
  'overused-words': 'Crutch words and stock gestures (just, really, nodded, looked…) used more than a rough guide allows, and words that crowd one scene compared with the rest of the book.',
  tense: 'A sentence in the present tense in a scene told in the past, or the other way round (dialogue aside), and a scene told in a different tense from the rest of the book.',
})[c];

/** Whether a built-in check is exact on the web, or approximate without Apple's language tagger. */
export const styleCheckIsExact = (c: BuiltInStyleCheck, tagger: LanguageTagger = approximateTagger): boolean =>
  tagger.exact || c === 'filter-verbs' || c === 'dialogue-tags' || c === 'tense';

/** A candidate for a second look (`StyleFinding`). */
export interface StyleFinding {
  id: string;
  /** A built-in check's raw value, or `rule:<id>` for a personal rule. */
  check: string;
  checkName: string;
  fragmentID: string;
  fragmentTitle: string;
  message: string;
  /** The passage, Unicode-scalar offsets into the fragment's text. */
  range: [number, number];
  quote: string;
  /** The sentence around the passage, to read it in place. */
  context: string;
}

export const ruleCheck = (id: string) => 'rule:' + id;

/** One manuscript text to check (`StyleText`): its Markdown with span classes blanked (same length, so offsets hold,
 *  but "yellow" isn't a word the checks count). */
export interface StyleText { id: string; title: string; text: string }

export function styleText(id: string, title: string, text: string): StyleText {
  return { id, title, text: blankingSpanClasses(text) };
}

/** Span classes and links' syntax (`[`, `](address)`) blanked to spaces of the same length, so findings keep their
 *  offsets and an address's "bit.ly" is never an -ly adverb. */
export function blankingSpanClasses(text: string): string {
  const ranges = [...spanClassRanges(text), ...linkSyntaxRanges(text)];
  if (!ranges.length) return text;
  const units = text.split('');
  for (const [a, b] of ranges) for (let k = a; k < b; k++) units[k] = ' ';
  return units.join('');
}

/** `StyleLinter.texts`: the manuscript's prose scenes in reading order, those with text. */
export function styleTexts(scenes: readonly { id: string }[], fragment: (id: string) => { title: string; text: string } | null): StyleText[] {
  const out: StyleText[] = [];
  for (const s of scenes) {
    const f = fragment(s.id);
    if (f && f.text !== '') out.push(styleText(s.id, f.title, f.text));
  }
  return out;
}

/** `StyleLinter.check`: runs the enabled built-in checks. `names` are character and place names (and aliases), which
 *  repeat by nature and are never flagged. `tagger` stands in for `NLTagger` (the approximate fallback by default). */
export function checkStyle(texts: readonly StyleText[], checks: Iterable<BuiltInStyleCheck>, names: readonly string[] = [],
  tagger: LanguageTagger = approximateTagger): StyleFinding[] {
  const enabled = new Set(checks);
  const findings: StyleFinding[] = [];
  const nameWords = new Set(names.flatMap((n) => splitOnNonLetters(n.toLowerCase())));
  // British manuscripts put dialogue in single quotes; the checks read speech as the Statistics dialogue card does.
  const style = quoteStyleOf(texts.map((t) => t.text));
  const scanned: { text: StyleText; paragraphs: StyleParagraph[]; scene: TenseScene }[] = [];
  for (const text of texts) {
    const paragraphs = splitParagraphs(text.text, style);
    const pending: Pending[] = [];
    if (enabled.has('tense')) scanned.push({ text, paragraphs, scene: scanTense(paragraphs, quoted) });
    if (enabled.has('repeated-words')) for (const p of paragraphs) pending.push(...repeatedWords(p, nameWords, tagger));
    if (enabled.has('filter-verbs')) for (const p of paragraphs) pending.push(...filterVerbs(p));
    if (enabled.has('dialogue-tags')) pending.push(...dialogueTags(paragraphs, text.title));
    findings.push(...resolvePending(pending, text, paragraphs));
  }
  if (enabled.has('overused-words')) findings.push(...overusedWords(texts, nameWords, tagger));
  // Tense needs every scene's tally before it can say which is told against the book.
  tenseFindings(scanned.map((s) => s.scene)).forEach((notes, i) => {
    if (!notes.length) return;
    const { text, paragraphs } = scanned[i];
    findings.push(...resolvePending(notes.map((n) => pendingIn('tense', paragraphs[n.paragraph], n.range, n.message)), text, paragraphs));
  });
  return findings;
}

/** `split(whereSeparator: { !$0.isLetter })`, empty pieces omitted. */
function splitOnNonLetters(s: string): string[] {
  const out: string[] = [];
  let current = '';
  for (const c of graphemes(s)) {
    if (/^\p{Alphabetic}/u.test(c)) current += c;
    else if (current) { out.push(current); current = ''; }
  }
  if (current) out.push(current);
  return out;
}

// MARK: Paragraphs

export interface StyleParagraph {
  /** The paragraph and its UTF-16 range in the fragment's text. */
  text: string;
  range: Range16;
  style: QuoteStyle;
}

/** `Paragraph.split`: lines with text, scene breaks and fences left out. */
export function splitParagraphs(text: string, style: QuoteStyle = 'double'): StyleParagraph[] {
  const out: StyleParagraph[] = [];
  let location = 0;
  for (const piece of text.split('\n')) {
    const trimmed = trimCFWhitespace(piece);
    if (trimmed !== '' && trimmed !== '***' && trimmed !== '* * *' && !trimmed.startsWith(':::')) {
      out.push({ text: piece, range: [location, location + piece.length], style });
    }
    location += piece.length + 1;
  }
  return out;
}

const quoted = (p: StyleParagraph) => quotedRanges(p.text, p.style);

/** A finding before its offsets are converted: UTF-16 in the fragment's text. */
export interface Pending { check: BuiltInStyleCheck; range: Range16; message: string }

const pendingIn = (check: BuiltInStyleCheck, p: StyleParagraph, local: Range16, message: string): Pending =>
  ({ check, range: [p.range[0] + local[0], p.range[0] + local[1]], message });

const trimWhitespaceAndNewlines = (s: string) => {
  const chars = Array.from(s);
  let a = 0, b = chars.length;
  while (a < b && isCFWhitespaceOrNewline(chars[a].codePointAt(0)!)) a++;
  while (b > a && isCFWhitespaceOrNewline(chars[b - 1].codePointAt(0)!)) b--;
  return chars.slice(a, b).join('');
};

/** `stableID`: SHA-256 of the parts joined by U+001F, first 8 bytes as hex. */
export const stableID = (...parts: string[]) => sha256Hex(parts.join('\u001F'), 8);

/** `StyleLinter.resolve`: scalar ranges and the sentence around each. */
export function resolvePending(pending: readonly Pending[], text: StyleText, paragraphs: StyleParagraph[]): StyleFinding[] {
  if (!pending.length) return [];
  const offsets = scalarOffsetsOfUTF16(text.text, pending.flatMap((p) => p.range));
  const sentences = new Map<number, Range16[]>();
  return pending.map((p, k) => {
    const range: [number, number] = [offsets[2 * k], offsets[2 * k + 1]];
    const quote = text.text.slice(p.range[0], p.range[1]);
    let context = quote;
    const i = paragraphIndex(p.range[0], paragraphs);
    if (i !== null) {
      const paragraph = paragraphs[i];
      if (!sentences.has(i)) sentences.set(i, sentenceRanges(paragraph.text));
      const local: Range16 = [p.range[0] - paragraph.range[0], p.range[1] - paragraph.range[0]];
      context = sentenceAround(local, paragraph.text, sentences.get(i)!);
    }
    return {
      id: stableID(p.check, text.id, String(range[0]), quote, p.message), check: p.check, checkName: styleCheckName(p.check),
      fragmentID: text.id, fragmentTitle: text.title, message: p.message, range, quote, context,
    };
  });
}

/** The paragraph (of those `splitParagraphs` made, in order) that holds a UTF-16 location. */
export function paragraphIndex(location: number, paragraphs: readonly StyleParagraph[]): number | null {
  let low = 0, high = paragraphs.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (paragraphs[mid].range[0] <= location) low = mid + 1; else high = mid;
  }
  if (low === 0) return null;
  const r = paragraphs[low - 1].range;
  return location >= r[0] && location < r[1] ? low - 1 : null;
}

/** The sentence (or sentences) of `text` that `range` falls in. */
export function sentenceAround(range: Range16, text: string, sentences: Range16[] = sentenceRanges(text)): string {
  const covering = sentences.filter((s) => intersects(s, range) || (range[0] >= s[0] && range[0] < s[1]));
  if (!covering.length) return text;
  return trimWhitespaceAndNewlines(text.slice(covering[0][0], covering[covering.length - 1][1]));
}

// MARK: Repeated words

/** Common words that repeat by nature (`StyleLinter.common`). */
export const STYLE_COMMON: ReadonlySet<string> = new Set(`
  about above after again against also although always among another around away back because been before being
  below between both came come could does doing done down during each either else even ever every from further
  going gone have having here into itself just know knew last least like little made make many maybe more most
  much must myself never next nothing once only other ought over quite rather really said same seem shall should
  since some something still such take than that their them themselves then there these they thing things think
  this those though through thought time together told took toward towards under until upon very want wanted
  well went were what whatever when where whether which while whom whose with within without would your yours
  yourself again said says tell get got just okay yeah`.split(/\s+/).filter(Boolean));

/** The fallback's stems of the common words, so "timed" is as common as "time" (as the Mac's lemma "time" is). */
const STYLE_COMMON_STEMS = new Set([...STYLE_COMMON].map(stemKey));

const CONTENT: ReadonlySet<LexicalClass> = new Set(['noun', 'verb', 'adjective', 'adverb']);
/** Word tokens as `NLTagger` enumerates them (punctuation and whitespace omitted). */
const TOKEN = /[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}\p{N}]+)*/gu;

function repeatedWords(p: StyleParagraph, names: Set<string>, tagger: LanguageTagger): Pending[] {
  const occurrences = new Map<string, { range: Range16; position: number }[]>();
  let position = 0;
  for (const m of p.text.matchAll(TOKEN)) {
    position++;
    const word = m[0];
    const lower = word.toLowerCase();
    const range: Range16 = [m.index!, m.index! + word.length];
    const chars = graphemes(word);
    // Capitalised words are names (or begin a sentence): skipped.
    if (chars.length < 5 || !charIsLowercase(chars[0]) || !chars.every((c) => /^\p{Alphabetic}/u.test(c) || c === '’' || c === "'")) continue;
    if (STYLE_COMMON.has(lower) || names.has(lower)) continue;
    const tag = tagger.lexicalClass(p.text, range);
    if (!tag || !CONTENT.has(tag)) continue;
    const lemma = tagger.lemma(p.text, range)?.toLowerCase() ?? lower;
    if (STYLE_COMMON.has(lemma) || (!tagger.exact && STYLE_COMMON_STEMS.has(lemma))) continue;
    const list = occurrences.get(lemma);
    if (list) list.push({ range, position }); else occurrences.set(lemma, [{ range, position }]);
  }
  // Three or more in the paragraph, or two within about a sentence or two of each other.
  const echoes: Range16[][] = [];
  for (const list of occurrences.values()) {
    if (list.length >= 3) echoes.push(list.map((o) => o.range));
    else if (list.length === 2 && list[1].position - list[0].position <= 40) echoes.push(list.map((o) => o.range));
  }
  return echoes.sort((a, b) => a[1][0] - b[1][0]).map((ranges) => {
    const forms = [...new Set(ranges.map(([a, b]) => p.text.slice(a, b).toLowerCase()))];
    const message = ranges.length === 2
      ? `“${forms.join('” and “')}” twice in this paragraph.`
      : `“${forms.join('”, “')}” ${ranges.length} times in this paragraph.`;
    return pendingIn('repeated-words', p, ranges[1], message);
  });
}

// MARK: Filter verbs

/** ICU's `\w` (UREGEX default word characters) and `\b` between them. */
const W = String.raw`[\p{Alphabetic}\p{M}\p{Nd}\p{Pc}‌‍]`;
const B = String.raw`(?:(?<=${W})(?!${W})|(?<!${W})(?=${W}))`;
const PERSON = String.raw`(?:I|he|she|they|we|you|[A-Z][a-z]+)`;

const FILTER_PATTERN = new RegExp(
  String.raw`${B}${PERSON}\s+(?:could\s+|would\s+|had\s+|didn’t\s+|didn't\s+)?(saw|see|sees|seen|watched|watches|noticed|notices|heard|hears|felt|feels|realised|realized|realises|realizes|wondered|wonders|smelled|smelt|tasted|observed|sensed|seemed to)${B}`,
  'dgu');

function filterVerbs(p: StyleParagraph): Pending[] {
  const dialogue = quoted(p);
  const out: Pending[] = [];
  for (const m of p.text.matchAll(FILTER_PATTERN)) {
    const verb = m.indices![1] as Range16;
    if (dialogue.some((d) => intersects(d, verb))) continue;
    const word = p.text.slice(verb[0], verb[1]);
    out.push(pendingIn('filter-verbs', p, verb, `“${word}” filters the moment through a character. Would showing it directly be closer?`));
  }
  return out;
}

// MARK: Dialogue tags

const SPEECH_VERBS = 'said|says|asked|asks|replied|answered|whispered|shouted|muttered|murmured|cried|exclaimed|hissed|snapped|growled|laughed|sighed|called|added|continued|yelled|screamed|demanded|insisted|retorted|mumbled|stammered|breathed|barked|chuckled|grunted|sneered|purred|snarled|gasped|groaned|spat|smiled|grinned';
/** Verbs that tell no more than "said" or "asked" needs to. */
const PLAIN_TAGS: ReadonlySet<string> = new Set(['said', 'says', 'asked', 'asks', 'replied', 'answered', 'added', 'continued', 'called', 'told']);
const NOT_ADVERBS: ReadonlySet<string> = new Set(['only', 'early', 'family', 'holy', 'silly', 'ugly', 'belly']);

function tagPattern(close: string, open: string): RegExp {
  return new RegExp(
    String.raw`(?:[${close}]\s*,?\s*(?:${PERSON}\s+(${SPEECH_VERBS})|(${SPEECH_VERBS})\s+${PERSON})${B}(\s+[a-z]+ly${B})?)|(?:${B}${PERSON}\s+(${SPEECH_VERBS})(\s+[a-z]+ly)?\s*[,:]\s*[${open}])`,
    'dgu');
}
const DOUBLE_TAGS = tagPattern('”"', '“"');
const SINGLE_TAGS = tagPattern('’', '‘');

function dialogueTags(paragraphs: StyleParagraph[], title: string): Pending[] {
  const findings: Pending[] = [];
  let dialogue = 0, tagged = 0;
  let firstTag: [StyleParagraph, Range16] | null = null;
  for (const p of paragraphs) {
    if (!quoted(p).length) continue;
    dialogue++;
    const matches = [...p.text.matchAll(p.style === 'single' ? SINGLE_TAGS : DOUBLE_TAGS)];
    if (!matches.length) continue;
    tagged++;
    for (const m of matches) {
      const verbRange = ([1, 2, 4].map((k) => m.indices![k]).find((r) => r !== undefined) ?? m.indices![0]) as Range16;
      const adverbRange = [3, 5].map((k) => m.indices![k]).find((r) => r !== undefined) as Range16 | undefined;
      if (!firstTag) firstTag = [p, verbRange];
      const verb = p.text.slice(verbRange[0], verbRange[1]);
      if (adverbRange && !NOT_ADVERBS.has(trimCFWhitespace(p.text.slice(adverbRange[0], adverbRange[1])))) {
        const whole: Range16 = [Math.min(verbRange[0], adverbRange[0]), Math.max(verbRange[1], adverbRange[1])];
        findings.push(pendingIn('dialogue-tags', p, whole, `“${p.text.slice(whole[0], whole[1])}”: does the line already carry how it was said?`));
      } else if (!PLAIN_TAGS.has(verb)) {
        findings.push(pendingIn('dialogue-tags', p, verbRange, `“${verb}” as a dialogue tag: would “said”, or no tag, do?`));
      }
    }
  }
  if (dialogue >= 6 && tagged / dialogue >= 0.7 && firstTag) {
    findings.unshift(pendingIn('dialogue-tags', firstTag[0], firstTag[1],
      `${tagged} of ${dialogue} lines of dialogue in “${title}” carry a tag. Could some go, where the speaker is clear?`));
  }
  return findings;
}

// MARK: Settings (StyleSettings.swift)

/** A habit the writer wants to catch, taught by example (`StyleRule`). */
export interface StyleRule {
  id: string;
  name: string;
  enabled: boolean;
  examples: string[];
  counterexamples: string[];
  /** The on-device model's one-sentence description of the habit. */
  pattern?: string;
}

export interface StyleSettings { builtIns: Set<BuiltInStyleCheck>; rules: StyleRule[] }

/** `ProjectDocument.styleSettings()`: built-in toggles are booleans in `settings.style`; each rule is JSON under
 *  `settings.styleRules` (unreadable rules left out), sorted by name as Finder sorts. */
export function styleSettings(project: ProjectRecord): StyleSettings {
  const builtIns = new Set<BuiltInStyleCheck>();
  const style = project.settings.style;
  if (style && typeof style === 'object') {
    for (const c of BUILT_IN_STYLE_CHECKS) if ((style as Record<string, unknown>)[c] === true) builtIns.add(c);
  }
  const rules: StyleRule[] = [];
  const stored = project.settings.styleRules;
  if (stored && typeof stored === 'object') {
    for (const json of Object.values(stored as Record<string, unknown>)) {
      if (typeof json !== 'string') continue;
      try {
        const r = JSON.parse(json) as Record<string, unknown>;
        if (typeof r.id !== 'string' || typeof r.name !== 'string' || typeof r.enabled !== 'boolean') continue;
        const strings = (v: unknown) => (Array.isArray(v) && v.every((x) => typeof x === 'string') ? v as string[] : null);
        const examples = strings(r.examples), counterexamples = strings(r.counterexamples);
        if (!examples || !counterexamples) continue;
        rules.push({ id: r.id, name: r.name, enabled: r.enabled, examples, counterexamples, ...(typeof r.pattern === 'string' ? { pattern: r.pattern } : {}) });
      } catch {
        continue;
      }
    }
  }
  rules.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  return { builtIns, rules };
}
